import { useState, useEffect } from 'react';
import { Loading } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { format } from 'date-fns';
import { supabase } from '../../../services/supabase';

interface UserSessionListProps {
  userId: string;
  refreshKey?: number;
}

export const UserSessionList = ({
  userId,
  refreshKey,
}: UserSessionListProps) => {
  const [latestSessionId, setLatestSessionId] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestSession = async () => {
      try {
        setLoading(true);

        // Fetch the most recent exposure to get the latest session ID (bypass React-Admin cache)
        const { data: latestExposures, error: latestError } = await supabase
          .from('exposures')
          .select('session_id, served_at')
          .eq('user_id', userId)
          .order('served_at', { ascending: false })
          .limit(1);

        if (latestError) throw latestError;

        if (!latestExposures || latestExposures.length === 0) {
          setLatestSessionId(null);
          setSessionData([]);
          setLoading(false);
          return;
        }

        const sessionId = latestExposures[0].session_id;
        setLatestSessionId(sessionId);

        // Fetch all exposures for this session (bypass React-Admin cache)
        const { data: sessionExposures, error: sessionError } = await supabase
          .from('exposures')
          .select('*')
          .eq('user_id', userId)
          .eq('session_id', sessionId)
          .order('served_at', { ascending: false });

        if (sessionError) throw sessionError;

        // Sort by served_at DESC, then by index DESC (within same timestamp)
        const sortedData = [...(sessionExposures || [])].sort((a, b) => {
          // First sort by served_at DESC
          const timeCompare =
            new Date(b.served_at).getTime() - new Date(a.served_at).getTime();
          if (timeCompare !== 0) return timeCompare;

          // If same timestamp, sort by index DESC (nulls last)
          if (a.index === null && b.index === null) return 0;
          if (a.index === null) return 1;
          if (b.index === null) return -1;
          return b.index - a.index;
        });

        setSessionData(sortedData);
      } catch {
        // Silently handle errors - this is a debug utility
        setLatestSessionId(null);
        setSessionData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestSession();
  }, [userId, refreshKey]);

  if (loading) {
    return <Loading />;
  }

  if (!latestSessionId || sessionData.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No session data found for this user
        </Typography>
      </Box>
    );
  }

  // Group by served_at timestamp
  const groupedByTime = sessionData.reduce(
    (acc, record) => {
      const timestamp = record.served_at;
      if (!acc[timestamp]) {
        acc[timestamp] = [];
      }
      acc[timestamp].push(record);
      return acc;
    },
    {} as Record<string, any[]>
  );

  return (
    <Box>
      {/* Session header */}
      <Box
        sx={{ mb: 2, px: 2, py: 1, bgcolor: 'action.hover', borderRadius: 1 }}
      >
        <Typography variant="body2" fontWeight="medium">
          Latest Session
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Session ID: {latestSessionId}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
          {sessionData.length} exposure(s)
        </Typography>
      </Box>

      {/* Compact table */}
      <Box sx={{ px: 2 }}>
        {Object.entries(groupedByTime).map(([timestamp, records]) => (
          <Box
            key={timestamp}
            sx={{
              display: 'flex',
              borderTop: '1px solid',
              borderColor: 'divider',
              py: 1,
            }}
          >
            {/* Left: Date/time split into 3 lines */}
            <Box sx={{ minWidth: 90, pr: 2 }}>
              <Typography
                variant="caption"
                display="block"
                sx={{ lineHeight: 1.2, fontSize: '0.75rem' }}
              >
                Served
              </Typography>
              <Typography
                variant="caption"
                display="block"
                sx={{ lineHeight: 1.2, fontSize: '0.75rem' }}
              >
                {format(new Date(timestamp), 'yyyy-MM-dd')}
              </Typography>
              <Typography
                variant="caption"
                display="block"
                sx={{ lineHeight: 1.2, fontSize: '0.75rem' }}
              >
                {format(new Date(timestamp), 'HH:mm:ss')}
              </Typography>
            </Box>

            {/* Right: Story list */}
            <Box
              component="ul"
              sx={{ m: 0, pl: 2, listStyleType: 'disc', flex: 1 }}
            >
              {records.map(record => (
                <Box
                  component="li"
                  key={`${record.session_id}-${record.story_slug}`}
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    lineHeight: 1.3,
                    color: 'text.primary',
                  }}
                >
                  {record.story_slug}
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>

    </Box>
  );
};
